import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";
import { COMPARE_GALLERY, SHOWCASE_GALLERY } from "../data";
import { SectionHeading } from "./SectionHeading";

const CompareSlider = ({ item, index }) => {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const updateFromClientX = (clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    let pct = ((clientX - rect.left) / rect.width) * 100;
    pct = Math.max(2, Math.min(98, pct));
    setPos(pct);
  };

  const onMove = (e) => {
    if (!dragging.current) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    updateFromClientX(clientX);
  };

  const start = () => {
    dragging.current = true;
  };
  const end = () => {
    dragging.current = false;
  };

  return (
    <motion.div
      data-testid={`compare-slider-${index}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6 }}
      className="relative w-full"
    >
      <div
        ref={containerRef}
        className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden border border-white/10 select-none cursor-ew-resize"
        onMouseMove={onMove}
        onMouseUp={end}
        onMouseLeave={end}
        onMouseDown={(e) => {
          start();
          updateFromClientX(e.clientX);
        }}
        onTouchStart={start}
        onTouchMove={onMove}
        onTouchEnd={end}
      >
        {/* After (full) */}
        <img
          data-testid={`compare-after-image-${index}`}
          src={item.after}
          alt={`${item.label} after detailing`}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: item.objectPosition || "center 55%" }}
          draggable={false}
        />
        <span className="absolute bottom-4 right-4 z-20 font-mono text-[10px] uppercase tracking-[0.2em] text-white bg-black/50 border border-white/15 rounded-full px-3 py-1 backdrop-blur-sm">
          After
        </span>

        {/* Before (clipped) */}
        <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
          <img
            data-testid={`compare-before-image-${index}`}
            src={item.before}
            alt={`${item.label} before detailing`}
            className="absolute inset-0 h-full object-cover"
            style={{ width: `${containerRef.current?.offsetWidth || 600}px`, maxWidth: "none", objectPosition: item.objectPosition || "center 55%" }}
            draggable={false}
          />
          <div className="absolute inset-0 bg-black/10" />
          <span className="absolute bottom-4 left-4 z-20 font-mono text-[10px] uppercase tracking-[0.2em] text-white bg-black/50 border border-white/15 rounded-full px-3 py-1 backdrop-blur-sm">
            Before
          </span>
        </div>

        {/* Handle */}
        <div className="absolute top-0 bottom-0 z-30 w-0.5 bg-white/90" style={{ left: `${pos}%` }}>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white text-[#050505] flex items-center justify-center shadow-xl">
            <MoveHorizontal size={18} />
          </div>
        </div>
      </div>
      <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
        {item.label}
      </p>
    </motion.div>
  );
};

const ShowcaseCard = ({ item, index }) => (
  <motion.div
    data-testid={`showcase-${index}`}
    initial={{ opacity: 0, y: 26 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.5, delay: (index % 4) * 0.06 }}
    whileHover={{ y: -6 }}
    className="group relative rounded-2xl overflow-hidden border border-white/10 aspect-[3/4]"
  >
    <img
      data-testid={`showcase-image-${index}`}
      src={item.image}
      alt={item.label}
      className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
      draggable={false}
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/85 via-transparent to-transparent" />
    <span className="absolute top-4 left-4 z-20 font-mono text-[10px] uppercase tracking-[0.2em] text-white bg-black/50 border border-white/15 rounded-full px-3 py-1 backdrop-blur-sm">
      {item.tag}
    </span>
    <p className="absolute bottom-4 left-4 right-4 z-20 font-heading text-lg font-semibold text-white">
      {item.label}
    </p>
  </motion.div>
);

export const BeforeAfter = () => {
  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative z-10 py-24 md:py-32 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionHeading
          overline="Before & After"
          title="See the transformation for yourself"
          subtitle="Drag the sliders to reveal the difference our detailing makes. Real results, real vehicles from the Bay Area."
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
          {COMPARE_GALLERY.map((item, i) => (
            <CompareSlider key={i} item={item} index={i} />
          ))}
        </div>

        <div className="mt-20 md:mt-28">
          <SectionHeading
            overline="Featured Details"
            title="From daily drivers to exotics"
            subtitle="A look at some of the luxury and exotic vehicles we've had the pleasure to detail."
          />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {SHOWCASE_GALLERY.map((item, i) => (
              <ShowcaseCard key={item.image} item={item} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
